import { useState, useEffect } from "react";
import { api, toast } from "../App";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Edit, Trash2, Users } from "lucide-react";
import DatePicker from "@/components/DatePicker";

export default function Vendors({ user }) {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentVendor, setCurrentVendor] = useState(null);
  
  // Contact management states
  const [contactsDialogOpen, setContactsDialogOpen] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [contactFormOpen, setContactFormOpen] = useState(false);
  const [editContactMode, setEditContactMode] = useState(false);
  const [currentContact, setCurrentContact] = useState(null);
  
  const [formData, setFormData] = useState({
    vendor_number: "",
    vendor_name: "",
    vendor_type: "Standard",
    industry: "Fabricator",
    business_relation_start_date: "",
    phone: "",
    fax: "",
    website: "",
    time_zone: "EST",
    address_line1: "",
    address_line2: "",
    city: "",
    zip_code: "",
    country: "",
    state: ""
  });

  const [contactFormData, setContactFormData] = useState({
    first_name: "",
    last_name: "",
    business_phone: "",
    business_extension: "",
    mobile_phone: "",
    email: "",
    designation: "",
    roles: [],
    special_notes: ""
  });

  const roleOptions = ["Project", "Billing", "Management", "FDO", "Estimator", "Owner"];

  useEffect(() => {
    fetchVendors();
  }, []);

  const fetchVendors = async () => {
    try {
      setLoading(true);
      const response = await api.get("/vendors");
      setVendors(response.data);
    } catch (error) {
      toast.error("Failed to fetch vendors");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchContacts = async (vendorId) => {
    try {
      const response = await api.get(`/vendor-contacts?vendor_id=${vendorId}`);
      setContacts(response.data);
    } catch (error) {
      toast.error("Failed to fetch contacts");
      console.error(error);
    }
  };

  const formatPhoneNumber = (value) => {
    const cleaned = value.replace(/\D/g, '');
    const limited = cleaned.substring(0, 10);
    
    if (limited.length <= 3) return limited;
    if (limited.length <= 6) return `${limited.slice(0, 3)}-${limited.slice(3)}`;
    return `${limited.slice(0, 3)}-${limited.slice(3, 6)}-${limited.slice(6)}`;
  };

  const handlePhoneInput = (e, field) => {
    const formatted = formatPhoneNumber(e.target.value);
    if (field === 'phone' || field === 'fax') {
      setFormData({ ...formData, [field]: formatted });
    } else {
      setContactFormData({ ...contactFormData, [field]: formatted });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editMode) {
        await api.put(`/vendors/${currentVendor.id}`, formData);
        toast.success("Vendor updated successfully");
      } else {
        await api.post("/vendors", formData);
        toast.success("Vendor created successfully");
      }
      
      setDialogOpen(false);
      resetForm();
      fetchVendors();
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      toast.error(typeof errorDetail === 'string' ? errorDetail : "Failed to save vendor");
      console.error(error);
    }
  };

  const handleEdit = (vendor) => {
    setEditMode(true);
    setCurrentVendor(vendor);
    setFormData({
      vendor_number: vendor.vendor_number || "",
      vendor_name: vendor.vendor_name || "",
      vendor_type: vendor.vendor_type || "Standard",
      industry: vendor.industry || "Fabricator",
      business_relation_start_date: vendor.business_relation_start_date || "",
      phone: vendor.phone || "",
      fax: vendor.fax || "",
      website: vendor.website || "",
      time_zone: vendor.time_zone || "EST",
      address_line1: vendor.address_line1 || "",
      address_line2: vendor.address_line2 || "",
      city: vendor.city || "",
      zip_code: vendor.zip_code || "",
      country: vendor.country || "",
      state: vendor.state || ""
    });
    setDialogOpen(true);
  };

  const handleDelete = async (vendorId) => {
    if (!window.confirm("Are you sure you want to delete this vendor?")) {
      return;
    }

    try {
      await api.delete(`/vendors/${vendorId}`);
      toast.success("Vendor deleted successfully");
      fetchVendors();
    } catch (error) {
      toast.error("Failed to delete vendor");
      console.error(error);
    }
  };

  const resetForm = () => {
    setFormData({
      vendor_number: "",
      vendor_name: "",
      vendor_type: "Standard",
      industry: "Fabricator",
      business_relation_start_date: "",
      phone: "",
      fax: "",
      website: "",
      time_zone: "EST",
      address_line1: "",
      address_line2: "",
      city: "",
      zip_code: "",
      country: "",
      state: ""
    });
    setEditMode(false);
    setCurrentVendor(null);
  };

  const openCreateDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  // Contact Management Functions
  const openContactsDialog = (vendor) => {
    setSelectedVendor(vendor);
    fetchContacts(vendor.id);
    setContactsDialogOpen(true);
  };

  const openContactForm = () => {
    resetContactForm();
    setContactFormOpen(true);
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const payload = {
        ...contactFormData,
        vendor_id: selectedVendor.id
      };

      if (editContactMode) {
        await api.put(`/vendor-contacts/${currentContact.id}`, contactFormData);
        toast.success("Contact updated successfully");
      } else {
        await api.post("/vendor-contacts", payload);
        toast.success("Contact created successfully");
      }
      
      setContactFormOpen(false);
      resetContactForm();
      fetchContacts(selectedVendor.id);
    } catch (error) {
      toast.error("Failed to save contact");
      console.error(error);
    }
  };

  const handleEditContact = (contact) => {
    setEditContactMode(true);
    setCurrentContact(contact);
    setContactFormData({
      first_name: contact.first_name || "",
      last_name: contact.last_name || "",
      business_phone: contact.business_phone || "",
      business_extension: contact.business_extension || "",
      mobile_phone: contact.mobile_phone || "",
      email: contact.email || "",
      designation: contact.designation || "",
      roles: contact.roles || [],
      special_notes: contact.special_notes || ""
    });
    setContactFormOpen(true);
  };

  const handleDeleteContact = async (contactId) => {
    if (!window.confirm("Are you sure you want to delete this contact?")) {
      return;
    }

    try {
      await api.delete(`/vendor-contacts/${contactId}`);
      toast.success("Contact deleted successfully");
      fetchContacts(selectedVendor.id);
    } catch (error) {
      toast.error("Failed to delete contact");
      console.error(error);
    }
  };

  const resetContactForm = () => {
    setContactFormData({
      first_name: "",
      last_name: "",
      business_phone: "",
      business_extension: "",
      mobile_phone: "",
      email: "",
      designation: "",
      roles: [],
      special_notes: ""
    });
    setEditContactMode(false);
    setCurrentContact(null);
  };

  const handleRoleToggle = (role) => {
    const newRoles = contactFormData.roles.includes(role)
      ? contactFormData.roles.filter(r => r !== role)
      : [...contactFormData.roles, role];
    setContactFormData({ ...contactFormData, roles: newRoles });
  };

  return (
    <Layout user={user}>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-md3-display-small">Vendors</h1>
          <Button className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full" onClick={openCreateDialog}>
            <Plus className="mr-2 h-4 w-4" /> Add Vendor
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-8">Loading vendors...</div>
        ) : (
          <div className="bg-md3-surface-container rounded-md3-lg shadow">
            <Table className="bg-md3-surface-container rounded-md3-md">
              <TableHeader>
                <TableRow>
                  <TableHead>Vendor Number</TableHead>
                  <TableHead>Vendor Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Industry</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Country</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {vendors.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-md3-on-surface-variant">
                      No vendors found. Click "Add Vendor" to create one.
                    </TableCell>
                  </TableRow>
                ) : (
                  vendors.map((vendor) => (
                    <TableRow key={vendor.id}>
                      <TableCell className="font-medium">{vendor.vendor_number}</TableCell>
                      <TableCell>{vendor.vendor_name}</TableCell>
                      <TableCell>{vendor.vendor_type}</TableCell>
                      <TableCell>{vendor.industry}</TableCell>
                      <TableCell>{vendor.phone}</TableCell>
                      <TableCell>{vendor.country}</TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" className="hover:bg-md3-surface-container-high rounded-md3-full transition-all duration-200" size="sm"
                           onClick={() => openContactsDialog(vendor)}>
                          <Users className="h-4 w-4" />
                        </Button>
                        <Button variant="outline" className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full" size="sm"
                           onClick={() => handleEdit(vendor)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button variant="destructive" className="bg-red-600 hover:bg-red-700 text-white shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full" size="sm"
                           onClick={() => handleDelete(vendor.id)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        )}

        {/* Vendor Create/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-white">
            <DialogHeader>
              <DialogTitle className="text-md3-headline-small text-md3-on-surface font-semibold ">{editMode ? "Edit Vendor" : "Add Vendor"}</DialogTitle>
              <DialogDescription className="text-md3-body-medium text-gray-600">
                {editMode ? "Update vendor information" : "Create a new vendor"}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Basic Information */}
              <div className="space-y-6">
                <h3 className="font-semibold text-lg">Basic Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="vendor_number" className="text-md3-label-large text-gray-700 font-medium">Vendor Number</Label>
                    <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="vendor_number"
                      value={formData.vendor_number}
                      onChange={(e) => setFormData({ ...formData, vendor_number: e.target.value })}
                      placeholder="Auto-generated if left empty (V001, V002...)"
                      disabled={editMode}
                    />
                    <p className="text-xs text-md3-on-surface-variant">Auto-generated starting with V001</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="vendor_name" className="text-md3-label-large text-gray-700 font-medium">Vendor Name *</Label>
                    <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="vendor_name"
                      value={formData.vendor_name}
                      onChange={(e) => setFormData({ ...formData, vendor_name: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="vendor_type" className="text-md3-label-large text-gray-700 font-medium">Vendor Type *</Label>
                    <Select
                      value={formData.vendor_type}
                      onValueChange={(value) => setFormData({ ...formData, vendor_type: value })}
                    >
                      <SelectTrigger className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Standard">Standard</SelectItem>
                        <SelectItem value="Premium">Premium</SelectItem>
                        <SelectItem value="Basic">Basic</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="industry" className="text-md3-label-large text-gray-700 font-medium">Industry *</Label>
                    <Select
                      value={formData.industry}
                      onValueChange={(value) => setFormData({ ...formData, industry: value })}
                    >
                      <SelectTrigger className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Fabricator">Fabricator</SelectItem>
                        <SelectItem value="Detailer">Detailer</SelectItem>
                        <SelectItem value="Engineering">Engineering</SelectItem>
                        <SelectItem value="Construction">Construction</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="business_relation_start_date" className="text-md3-label-large text-gray-700 font-medium">Business Relation Start Date</Label>
                    <DatePicker
                      date={formData.business_relation_start_date}
                      setDate={(date) => setFormData({ ...formData, business_relation_start_date: date })}
                    />
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              <div className="space-y-6">
                <h3 className="font-semibold text-lg">Contact Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-md3-label-large text-gray-700 font-medium">Phone</Label>
                    <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="phone"
                      value={formData.phone}
                      onChange={(e) => handlePhoneInput(e, 'phone')}
                      maxLength={12}
                      placeholder="222-333-4444"
                    />
                    <p className="text-xs text-md3-on-surface-variant">Format: XXX-XXX-XXXX (numbers only)</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="fax" className="text-md3-label-large text-gray-700 font-medium">Fax</Label>
                    <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="fax"
                      value={formData.fax}
                      onChange={(e) => handlePhoneInput(e, 'fax')}
                      maxLength={12}
                      placeholder="222-333-4444"
                    />
                    <p className="text-xs text-md3-on-surface-variant">Format: XXX-XXX-XXXX (numbers only)</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="website" className="text-md3-label-large text-gray-700 font-medium">Website</Label>
                    <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="website"
                      type="text"
                      value={formData.website}
                      onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                      placeholder="www.vendor.com"
                    />
                    <p className="text-xs text-md3-on-surface-variant">Enter without https:// (e.g., www.vendor.com)</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="time_zone" className="text-md3-label-large text-gray-700 font-medium">Time Zone</Label>
                    <Select
                      value={formData.time_zone}
                      onValueChange={(value) => setFormData({ ...formData, time_zone: value })}
                    >
                      <SelectTrigger className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="EST">EST</SelectItem>
                        <SelectItem value="CST">CST</SelectItem>
                        <SelectItem value="MST">MST</SelectItem>
                        <SelectItem value="PST">PST</SelectItem>
                        <SelectItem value="IST">IST</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Address Information */}
              <div className="space-y-6">
                <h3 className="font-semibold text-lg">Address Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2 col-span-2">
                    <Label htmlFor="address_line1" className="text-md3-label-large text-gray-700 font-medium">Address Line 1</Label>
                    <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="address_line1"
                      value={formData.address_line1}
                      onChange={(e) => setFormData({ ...formData, address_line1: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2 col-span-2">
                    <Label htmlFor="address_line2" className="text-md3-label-large text-gray-700 font-medium">Address Line 2</Label>
                    <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="address_line2"
                      value={formData.address_line2}
                      onChange={(e) => setFormData({ ...formData, address_line2: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="city" className="text-md3-label-large text-gray-700 font-medium">City</Label>
                    <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="city"
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="state" className="text-md3-label-large text-gray-700 font-medium">State</Label>
                    <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="state"
                      value={formData.state}
                      onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="zip_code" className="text-md3-label-large text-gray-700 font-medium">ZIP Code</Label>
                    <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="zip_code"
                      value={formData.zip_code}
                      onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="country" className="text-md3-label-large text-gray-700 font-medium">Country</Label>
                    <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="country"
                      value={formData.country}
                      onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full" >
                  {editMode ? "Update Vendor" : "Create Vendor"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Contacts Dialog */}
        <Dialog open={contactsDialogOpen} onOpenChange={setContactsDialogOpen}>
          <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto bg-white">
            <DialogHeader>
              <DialogTitle className="text-md3-headline-small text-md3-on-surface font-semibold ">
                Contacts for {selectedVendor?.vendor_name}
              </DialogTitle>
              <DialogDescription className="text-md3-body-medium text-gray-600">
                Manage vendor contacts
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-6">
              <Button onClick={openContactForm} size="sm" className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full">
                <Plus className="mr-2 h-4 w-4" /> Add Contact
              </Button>

              <Table className="bg-md3-surface-container rounded-md3-md">
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Business Phone</TableHead>
                    <TableHead>Mobile Phone</TableHead>
                    <TableHead>Roles</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {contacts.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-4 text-md3-on-surface-variant">
                        No contacts found. Click "Add Contact" to create one.
                      </TableCell>
                    </TableRow>
                  ) : (
                    contacts.map((contact) => (
                      <TableRow key={contact.id}>
                        <TableCell>{contact.first_name} {contact.last_name}</TableCell>
                        <TableCell>{contact.email}</TableCell>
                        <TableCell>{contact.business_phone}</TableCell>
                        <TableCell>{contact.mobile_phone}</TableCell>
                        <TableCell>
                          {contact.roles && contact.roles.length > 0 
                            ? contact.roles.join(", ") 
                            : "-"}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button variant="outline" className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full" size="sm"
                             onClick={() => handleEditContact(contact)}>
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button variant="destructive" className="bg-red-600 hover:bg-red-700 text-white shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full" size="sm"
                             onClick={() => handleDeleteContact(contact.id)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </DialogContent>
        </Dialog>

        {/* Contact Form Dialog */}
        <Dialog open={contactFormOpen} onOpenChange={setContactFormOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white">
            <DialogHeader>
              <DialogTitle className="text-md3-headline-small text-md3-on-surface font-semibold ">{editContactMode ? "Edit Contact" : "Add Contact"}</DialogTitle>
              <DialogDescription className="text-md3-body-medium text-gray-600">
                {editContactMode ? "Update contact information" : "Create a new contact for this vendor"}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleContactSubmit} className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="first_name" className="text-md3-label-large text-gray-700 font-medium">First Name *</Label>
                  <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="first_name"
                    value={contactFormData.first_name}
                    onChange={(e) => setContactFormData({ ...contactFormData, first_name: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="last_name" className="text-md3-label-large text-gray-700 font-medium">Last Name *</Label>
                  <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="last_name"
                    value={contactFormData.last_name}
                    onChange={(e) => setContactFormData({ ...contactFormData, last_name: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="business_phone" className="text-md3-label-large text-gray-700 font-medium">Business Phone</Label>
                  <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="business_phone"
                    value={contactFormData.business_phone}
                    onChange={(e) => handlePhoneInput(e, 'business_phone')}
                    maxLength={12}
                    placeholder="222-333-4444"
                  />
                  <p className="text-xs text-md3-on-surface-variant">Format: XXX-XXX-XXXX (numbers only)</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="business_extension" className="text-md3-label-large text-gray-700 font-medium">Extension</Label>
                  <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="business_extension"
                    value={contactFormData.business_extension}
                    onChange={(e) => setContactFormData({ ...contactFormData, business_extension: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="mobile_phone" className="text-md3-label-large text-gray-700 font-medium">Mobile Phone</Label>
                  <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="mobile_phone"
                    value={contactFormData.mobile_phone}
                    onChange={(e) => handlePhoneInput(e, 'mobile_phone')}
                    maxLength={12}
                    placeholder="222-333-4444"
                  />
                  <p className="text-xs text-md3-on-surface-variant">Format: XXX-XXX-XXXX (numbers only)</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-md3-label-large text-gray-700 font-medium">Email</Label>
                  <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="email"
                    type="email"
                    value={contactFormData.email}
                    onChange={(e) => setContactFormData({ ...contactFormData, email: e.target.value })}
                  />
                </div>
                <div className="space-y-2 col-span-2">
                  <Label htmlFor="designation" className="text-md3-label-large text-gray-700 font-medium">Designation</Label>
                  <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="designation"
                    value={contactFormData.designation}
                    onChange={(e) => setContactFormData({ ...contactFormData, designation: e.target.value })}
                  />
                </div>
                <div className="space-y-2 col-span-2">
                  <Label>Roles</Label>
                  <div className="grid grid-cols-3 gap-3">
                    {roleOptions.map((role) => (
                      <div key={role} className="flex items-center space-x-2">
                        <Checkbox
                          id={`role-${role}`}
                          checked={contactFormData.roles.includes(role)}
                          onCheckedChange={() => handleRoleToggle(role)}
                        />
                        <label htmlFor={`role-${role}`} className="text-md3-body-small cursor-pointer">
                          {role}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="space-y-2 col-span-2">
                  <Label htmlFor="special_notes" className="text-md3-label-large text-gray-700 font-medium">Special Notes</Label>
                  <Textarea className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="special_notes"
                    value={contactFormData.special_notes}
                    onChange={(e) => setContactFormData({ ...contactFormData, special_notes: e.target.value })}
                    rows={3}
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full" onClick={() => setContactFormOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full" >
                  {editContactMode ? "Update Contact" : "Add Contact"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
